import { ImageFile } from '../shared';
import { Channel } from './channelsNew';
import { graphqlApiWithPersisted } from './graphqlApi';
import { userQuery } from './helpers';
import { OrganisationTag } from './organisation';

export enum UserGender {
  NOT_SELECTED = 'NOT_SELECTED', //* artificial field for display only
  FEMALE = 'FEMALE',
  MALE = 'MALE',
  COUPLES = 'COUPLES',
  NON_BINARY = 'NON_BINARY',
  TRANS = 'TRANS',
}

export enum UserRole {
  CHANNEL_READER = 'CHANNEL_READER',
  CHANNEL_EDITOR_LIMITED = 'CHANNEL_EDITOR_LIMITED',
  CHANNEL_EDITOR = 'CHANNEL_EDITOR',
  CHANNEL_ADMIN = 'CHANNEL_ADMIN',
  ORGANISATION_ADMIN = 'ORGANISATION_ADMIN',
  ORGANISATION_OWNER = 'ORGANISATION_OWNER',
  ORGANISATION_TEAM_MEMBER = 'ORGANISATION_TEAM_MEMBER',
}

export enum UserInvitedBy {
  UNKNOWN = 'UNKNOWN', //* artificial field for display only
  AUTO_GENERATED_PASSWORD = 'AUTO_GENERATED_PASSWORD',
  COMPANY_ID = 'COMPANY_ID',
  DIRECT_LINK = 'DIRECT_LINK',
  INVITATION = 'INVITATION',
}

export enum UserRoles {
  'admin' = 1,
  'editor' = 2,
  'editor-limited' = 3,
  'reader' = 5,
  'org-admin' = 7,
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  // ENGAGED = 'ENGAGED',
  INACTIVE = 'INACTIVE',
  BANNED = 'BANNED',
}

export enum ChannelRole {
  ADMIN = 1,
  EDITOR = 2,
  EDITOR_LIMITED = 3,
  READER = 5,
}

export enum OrganisationRole {
  OWNER = 6,
  ADMIN = 7,
  TEAM_MEMBER = 8,
}

interface UserLocation {
  text: string;
}

export interface LeaderBoardUserBadge {
  id: number;
  level: number;
  name: string;
  nameRender: string;
}

export interface UserSocialLinks {
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  snapchat?: string;
  tiktok?: string;
  twitter?: string;
  youtube?: string;
}

export interface UserChannelAccess {
  id: number;
  name: string;
  subdomain: string;
  role: UserRole;
}

interface UserStatistic {
  comments: number;
  likesOnComments: number;
  posts: number;
  reactions: number;
  shares: number;
}

export interface UserPreview {
  id: number;
  screenName: string;
  email?: string;
  avatar?: ImageFile;
  companyId?: string;
}

export interface User {
  id: number;
  bio: string;
  department: string;
  gender: UserGender | null;
  isAnonymous: boolean;
  isStaff: boolean;
  isBanned: boolean;
  links: UserSocialLinks;
  location: UserLocation;
  organisationTags: OrganisationTag[];
  phone?: string;
  position: string;
  roleId?: UserRole;
  screenName: string;
  subscription: string;
  url: string;
  registeredAt: string;
  accessedChannels: UserChannelAccess[];
  statistic: UserStatistic;
  referralUsersCount: number;
  email?: string;
  avatar?: ImageFile;
  leaderBoardBadge?: LeaderBoardUserBadge;
  authorizedAt?: string;
  companyId?: string;
  onboardingMethod?: UserInvitedBy;
  referredByUser?: UserPreview;
}

interface UserProfileResponse {
  data: { me: User };
}

interface UserByIdArgs {
  userId: number;
}

interface UserByIdResponse {
  data: {
    userById: {
      payload: User;
    };
  };
}

interface UserProfileUpdateArgs {
  fields: {
    bio?: string;
    department?: string;
    gender?: UserGender | null;
    imageId: number | null;
    links: UserSocialLinks;
    location?: { text: string };
    organisationTagsId?: number[];
    phone?: string;
    position?: string;
    screenName?: string;
    subscription?: string;
    url?: string;
  };
}

interface UserProfileUpdateResponse {
  data: {
    userProfileUpdate: {
      payload: User;
    };
  };
}

interface UserRegistrationByEmailArgs {
  email: string;
  screenName: string;
  referralCode: string | null;
}

interface UserRegistrationByEmailResponseData {
  payload: boolean;
  error: [{ message: string }];
}

interface UserRegistrationByEmailResponse {
  data: {
    userRegistrationByEmail: UserRegistrationByEmailResponseData;
  };
}

interface UserLoginBySocialPayload {
  accessToken: string;
  channel: Channel;
}

interface UserLoginBySocialData {
  payload: UserLoginBySocialPayload;
  error: [{ message: string }];
}

interface UserLoginBySocialResponse {
  data: {
    userLoginBySocial: UserLoginBySocialData;
  };
}

interface UserLoginBySocialArgs {
  accessToken: string;
  type: string;
  referralCode: string | null;
  screenName?: string;
}

interface UserDeviceRegistrationArgs {
  fcmToken: string;
}

interface UserDeviceRegistrationResponse {
  data: {
    userDeviceRegistration: {
      payload: boolean;
    };
  };
}

interface UserLogoutArgs {
  fcmToken: string;
}

interface UserLogoutResponse {
  data: {
    userLogout: {
      payload: boolean;
    };
  };
}

interface UserLoginByEmailArgs {
  email: string;
  password: string;
  otp?: string;
  otpBackup?: string;
}

interface UserLoginByEmailPayload {
  accessToken: string;
  channel: Channel;
}

interface UserLoginByEmailData {
  payload: UserLoginByEmailPayload;
  error: {
    __typename: string;
    message: string;
    otpAuthSecret?: string;
    otpAuthUrlQRCode?: string;
  };
}

interface UserLoginByEmailResponse {
  data: {
    userLoginByEmail: UserLoginByEmailData;
  };
}

interface UserLoginByMagicLinkArgs {
  token: string;
  otp?: string;
  otpBackup?: string;
}

interface UserLoginByMagicLinkPayload {
  accessToken: string;
  channel: Channel;
}

interface UserLoginByMagicLinkData {
  payload: UserLoginByMagicLinkPayload;
  error: {
    __typename: string;
    message: string;
    otpAuthSecret?: string;
    otpAuthUrlQRCode?: string;
  };
}

interface UserLoginByMagicLinkResponse {
  data: {
    userLoginByMagicLink: UserLoginByEmailData;
  };
}

interface UserLoginByAfzArgs {
  email: string;
  password: string;
}

interface UserLoginByAfzPayload {
  accessToken: string;
  channel: Channel;
}

interface UserLoginByAfzData {
  payload: UserLoginByAfzPayload;
  error: {
    __typename: string;
    message: string;
  };
}

interface UserLoginByAfzResponse {
  data: {
    userLoginByAfz: UserLoginByAfzData;
  };
}

interface UserLoginByAfzTicketArgs {
  ticket: string;
}

interface UserLoginByAfzTicketData {
  payload: UserLoginByAfzPayload;
  error: {
    __typename: string;
    message: string;
  };
}

interface UserLoginByAfzTicketResponse {
  data: {
    userLoginByAfzTicket: UserLoginByAfzTicketData;
  };
}

interface UserRequestNew2FASetupPayload {
  otpAuthSecret: string;
  otpAuthUrlQRCode: string;
}

interface UserRequestNew2FASetupResponse {
  data: {
    userRequestNew2FASetup: {
      payload: UserRequestNew2FASetupPayload;
    };
  };
}

interface UserConfirmNew2FASetupArgs {
  otp: string;
}

interface UserConfirmNew2FASetupResponse {
  data: {
    userConfirmNew2FASetup: {
      payload: boolean;
    };
  };
}

interface UserLoginByAnonymousOrGetCurrentProfilePayload {
  accessToken: string;
  channel: Channel;
  user: User;
}

interface UserLoginByAnonymousOrGetCurrentProfileResponse {
  data: {
    userLoginByAnonymousOrGetCurrentProfile: {
      payload: UserLoginByAnonymousOrGetCurrentProfilePayload;
    };
  };
}

export enum UserOnboardingType {
  PERSONALIZED_NEWSFEED = 'PERSONALIZED_NEWSFEED',
  NEWSLETTER_CONSENT = 'NEWSLETTER_CONSENT',
}

interface MeOnboardingPendingStatusesResponse {
  data: { meOnboardingPendingStatuses: UserOnboardingType[] };
}

interface UserUpdateOnboardingStatusArgs {
  type: UserOnboardingType;
}

interface UserUpdateOnboardingStatusResponse {
  data: { userUpdateOnboardingStatus: { payload: boolean } };
}

export const userApi = graphqlApiWithPersisted
  .enhanceEndpoints({ addTagTypes: ['MeOnboardingStatus'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      me: builder.query<User, void>({
        query: () => ({
          url: '/graphql/webapp?me',
          method: 'POST',
          body: {
            query: `query Me {
              me {
                ${userQuery}
              }
            }`,
          },
        }),
        transformResponse: (response: UserProfileResponse) => response.data.me,
      }),
      userById: builder.query<User, UserByIdArgs>({
        query: ({ userId }) => ({
          url: '/graphql/webapp?userById',
          method: 'POST',
          body: {
            query: `query UserById($userId: Int!) {
              userById(userId: $userId) {
                payload {
                  ${userQuery}
                }
              }
            }`,
            variables: { userId },
          },
        }),
        transformResponse: (response: UserByIdResponse) => response.data.userById.payload,
      }),
      meOnboardingPendingStatuses: builder.query<UserOnboardingType[], void>({
        query: () => ({
          url: '/graphql/webapp?meOnboardingPendingStatuses',
          method: 'POST',
          body: {
            query: `query MeOnboardingPendingStatuses {
              meOnboardingPendingStatuses
            }`,
          },
        }),
        providesTags: ['MeOnboardingStatus'],
        transformResponse: (response: MeOnboardingPendingStatusesResponse) =>
          response.data.meOnboardingPendingStatuses,
      }),
      userUpdateOnboardingStatus: builder.mutation<boolean, UserUpdateOnboardingStatusArgs>({
        query: ({ type }) => ({
          url: '/graphql/webapp?userUpdateOnboardingStatus',
          method: 'POST',
          body: {
            query: `mutation UserUpdateOnboardingStatus($input: UserUpdateOnboardingStatusInput!) {
              userUpdateOnboardingStatus(input: $input) {
                payload
                error {
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: { type },
            },
          },
        }),
        invalidatesTags: ['MeOnboardingStatus'],
        transformResponse: (response: UserUpdateOnboardingStatusResponse) =>
          response.data.userUpdateOnboardingStatus.payload,
      }),
      userProfileUpdate: builder.mutation<User, UserProfileUpdateArgs>({
        query: ({ fields }) => ({
          url: '/graphql/webapp?userProfileUpdate',
          method: 'POST',
          body: {
            query: `mutation UserProfileUpdate($input: UserProfileUpdateInput!) {
              userProfileUpdate(input: $input) {
                payload {
                  ${userQuery}
                }
              }
            }`,
            variables: {
              input: { fields },
            },
          },
        }),
        transformResponse: (response: UserProfileUpdateResponse) =>
          response.data.userProfileUpdate.payload,
      }),
      userRegistrationByEmail: builder.mutation<
        UserRegistrationByEmailResponseData,
        UserRegistrationByEmailArgs
      >({
        query: ({ email, screenName, referralCode }) => ({
          url: '/graphql/webapp?userRegistrationByEmail',
          method: 'POST',
          body: {
            query: `mutation UserRegistrationByEmail($input: UserRegistrationByEmailInput!) {
              userRegistrationByEmail(input: $input) {
                payload
                error {
                  ... on UnknownError {
                    message
                  }
                  ... on UserIsAlreadyRegisteredError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: { email, screenName, referralCode },
            },
          },
        }),
        transformResponse: (response: UserRegistrationByEmailResponse) =>
          response.data.userRegistrationByEmail,
      }),
      userLoginByEmail: builder.mutation<UserLoginByEmailData, UserLoginByEmailArgs>({
        query: ({ email, password, otp, otpBackup }) => ({
          url: '/graphql/webapp?userLoginByEmail',
          method: 'POST',
          body: {
            query: `mutation UserLoginByEmail($input: UserLoginByEmailInputType!) {
              userLoginByEmail(input: $input) {
                payload {
                  accessToken
                  channel {
                    subdomain
                  }
                }
                error {
                  __typename
                  ... on UnknownError {
                    message
                  }
                  ... on User2FAOtpSetupIsRequiredError {
                    message
                    otpAuthSecret
                    otpAuthUrlQRCode
                  }
                  ... on User2FAOtpIsRequiredError {
                    message
                  }
                  ... on User2FAOtpIsInvalidError {
                    message
                  }
                  ... on User2FAOtpBackupIsUsedError {
                    message
                  }
                  ... on User2FAOtpBackupIsInvalidError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: {
                email,
                password,
                ...(Boolean(otp) && { otp }),
                ...(Boolean(otpBackup) && { otpBackup }),
              },
            },
          },
        }),
        transformResponse: (response: UserLoginByEmailResponse) => response.data.userLoginByEmail,
      }),
      userLoginBySocial: builder.mutation<UserLoginBySocialPayload, UserLoginBySocialArgs>({
        query: ({ accessToken, type, referralCode, screenName = '' }) => ({
          url: '/graphql/webapp?userLoginBySocial',
          method: 'POST',
          body: {
            query: `mutation UserLoginBySocial($input: UserLoginBySocialInputType!) {
              userLoginBySocial(input: $input) {
                payload {
                  accessToken
                  channel {
                    subdomain
                  }
                }
                error {
                  ... on SocialLoginFetchingProfileError {
                    message
                  }
                  ... on SocialLoginInvalidConnectionError {
                    message
                  }
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: { accessToken, type, referralCode, screenName },
            },
          },
        }),
        transformResponse: (response: UserLoginBySocialResponse) =>
          response.data.userLoginBySocial.payload,
      }),
      userLoginByMagicLink: builder.mutation<UserLoginByMagicLinkData, UserLoginByMagicLinkArgs>({
        query: ({ token, otp, otpBackup }) => ({
          url: '/graphql/webapp?userLoginByMagicLink',
          method: 'POST',
          body: {
            query: `mutation UserLoginByMagicLink($input: UserLoginByMagicLinkInputType!) {
              userLoginByMagicLink(input: $input) {
                payload {
                  accessToken
                  channel {
                    subdomain
                  }
                }
                error {
                  __typename
                  ... on UnknownError {
                    message
                  }
                  ... on User2FAOtpIsRequiredError {
                    message
                  }
                  ... on User2FAOtpIsInvalidError {
                    message
                  }
                  ... on User2FAOtpBackupIsUsedError {
                    message
                  }
                  ... on User2FAOtpBackupIsInvalidError {
                    message
                  }
                  ... on MagicLinkNotFoundError {
                    message
                  }
                  ... on MagicLinkExpiredError {
                    message
                  }
                  ... on MagicLinkAttemptsAreOverError {
                    message
                  }
                  ... on ChannelNotFoundError {
                    message
                  }
                }
              }
            }`,
            variables: {
              input: {
                token,
                ...(Boolean(otp) && { otp }),
                ...(Boolean(otpBackup) && { otpBackup }),
              },
            },
          },
        }),
        transformResponse: (response: UserLoginByMagicLinkResponse) =>
          response.data.userLoginByMagicLink,
      }),
      userLoginByAfz: builder.mutation<UserLoginByAfzData, UserLoginByAfzArgs>({
        query: ({ email, password }) => ({
          url: '/graphql/webapp?userLoginByAfz',
          method: 'POST',
          body: {
            query: `mutation UserLoginByAfz($input: UserLoginByAfzInput!) {
              userLoginByAfz(input: $input) {
                payload {
                  accessToken
                  channel {
                    subdomain
                  }
                }
                error {
                  __typename
                  ... on UnknownError {
                    message
                  }
                  ... on AfzUnknownCredentialError {
                    message
                  }
                  ... on AfzConnectionNotFoundError {
                    message
                  }
                }
              }
            }`,
            variables: { input: { email, password } },
          },
        }),
        transformResponse: (response: UserLoginByAfzResponse) => response.data.userLoginByAfz,
      }),
      userLoginByAfzTicket: builder.mutation<UserLoginByAfzTicketData, UserLoginByAfzTicketArgs>({
        query: ({ ticket }) => ({
          url: '/graphql/webapp?userLoginByAfzTicket',
          method: 'POST',
          body: {
            query: `mutation UserLoginByAfzTicket($input: UserLoginByAfzTicketInput!) {
              userLoginByAfzTicket(input: $input) {
                payload {
                  accessToken
                  channel {
                    subdomain
                  }
                }
                error {
                  __typename
                  ... on UnknownError {
                    message
                  }
                  ... on AfzUnknownCredentialError {
                    message
                  }
                  ... on AfzConnectionNotFoundError {
                    message
                  }
                }
              }
            }`,
            variables: { input: { ticket } },
          },
        }),
        transformResponse: (response: UserLoginByAfzTicketResponse) =>
          response.data.userLoginByAfzTicket,
      }),
      userDeviceRegistration: builder.mutation<boolean, UserDeviceRegistrationArgs>({
        query: ({ fcmToken }) => ({
          url: '/graphql/webapp?userDeviceRegistration',
          method: 'POST',
          body: {
            query: `mutation UserDeviceRegistration($input: UserDeviceRegistrationInput!) {
              userDeviceRegistration(input: $input) {
                payload
                error {
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: { input: { fcmToken } },
          },
        }),
        transformResponse: (response: UserDeviceRegistrationResponse) =>
          response.data.userDeviceRegistration.payload,
      }),
      userLogout: builder.mutation<boolean, UserLogoutArgs>({
        query: ({ fcmToken }) => ({
          url: '/graphql/webapp?userLogout',
          method: 'POST',
          body: {
            query: `mutation UserLogout($input: UserLogoutInput!){
              userLogout(input: $input) {
                payload
                error {
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
            variables: { input: { fcmToken } },
          },
        }),
        transformResponse: (response: UserLogoutResponse) => response.data.userLogout.payload,
      }),
      userDeactivate: builder.mutation<boolean, void>({
        query: () => ({
          url: '/graphql/webapp?userDeactivate',
          method: 'POST',
          body: {
            query: `mutation UserDeactivate {
              userDeactivate {
                payload
                error {
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
          },
        }),
        transformResponse: (response: UserLogoutResponse) => response.data.userLogout.payload,
      }),
      userRequestNew2FASetup: builder.mutation<UserRequestNew2FASetupPayload, void>({
        query: () => ({
          url: '/graphql/webapp?userRequestNew2FASetup',
          method: 'POST',
          body: {
            query: `mutation UserRequestNew2FASetup {
              userRequestNew2FASetup {
                payload {
                  otpAuthSecret
                  otpAuthUrlQRCode
                }
                error {
                  ... on UnknownError {
                    message
                  }
                }
              }
            }`,
          },
        }),
        transformResponse: (response: UserRequestNew2FASetupResponse) =>
          response.data.userRequestNew2FASetup.payload,
      }),
      userConfirmNew2FASetup: builder.mutation<boolean, UserConfirmNew2FASetupArgs>({
        query: ({ otp }) => ({
          url: '/graphql/webapp?userConfirmNew2FASetup',
          method: 'POST',
          body: {
            query: `mutation UserConfirmNew2FASetup($input: UserConfirmNew2faSetupInput!) {
              userConfirmNew2FASetup(input: $input) {
                payload
                error {
                  ... on UnknownError {
                    message
                  }
                  ... on User2FAOtpIsInvalidError {
                    message
                  }
                  ... on User2FATemporarySecretIsEmptyError {
                    message
                  }
                }
              }
            }`,
            variables: { input: { otp } },
          },
        }),
        transformResponse: (response: UserConfirmNew2FASetupResponse) =>
          response.data.userConfirmNew2FASetup.payload,
      }),
      userLoginByAnonymousOrGetCurrentProfile: builder.mutation<
        UserLoginByAnonymousOrGetCurrentProfilePayload,
        void
      >({
        query: () => ({
          url: '/graphql/webapp?userLoginByAnonymousOrGetCurrentProfile',
          method: 'POST',
          body: {
            query: `mutation UserLoginByAnonymousOrGetCurrentProfile {
              userLoginByAnonymousOrGetCurrentProfile {
                payload {
                  accessToken
                  user {
                    ${userQuery}
                  }
                }
              }
            }`,
          },
        }),
        transformResponse: (response: UserLoginByAnonymousOrGetCurrentProfileResponse) =>
          response.data.userLoginByAnonymousOrGetCurrentProfile.payload,
      }),
    }),
  });

export const {
  useMeQuery,
  useUserByIdQuery,
  useMeOnboardingPendingStatusesQuery,
  useUserUpdateOnboardingStatusMutation,
} = userApi;
